import type { LeadershipMetrics } from '@delivery-metrics/shared';
import { ShowChart, Timeline } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { InteractiveChart } from '../InteractiveChart';

interface FeatureDeliverySectionProps {
  data: LeadershipMetrics | null;
}

export const FeatureDeliverySection: React.FC<FeatureDeliverySectionProps> = ({
  data,
}) => {
  const targetFeaturesPerMonth = 10; // This could be configurable

  if (!data) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h5'
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
        >
          <ShowChart sx={{ mr: 1 }} />
          Feature Delivery Metrics
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant='h6'>
                  Loading feature delivery trends...
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant='h6'>
                  Loading delivery velocity...
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const currentMonthFeatures = data.kpis?.featuresDelivered?.value || 0;
  const achievementPercentage =
    (currentMonthFeatures / targetFeaturesPerMonth) * 100;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <ShowChart sx={{ mr: 1 }} />
        Feature Delivery Metrics
      </Typography>

      <Grid container spacing={3}>
        {/* Feature Delivery Trends */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <InteractiveChart
            title='Feature Delivery Trends'
            subtitle='Monthly feature completion over time'
            data={
              data.featureDeliveryTrends?.map(point => ({
                timestamp: point.timestamp,
                features: point.value,
                label: new Date(point.timestamp).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                }),
              })) || []
            }
            series={[
              { key: 'features', name: 'Features Delivered', color: '#1976d2' },
            ]}
            type='area'
            height={350}
            showGrid={true}
            enableDrillDown={true}
            xAxisKey='timestamp'
            yAxisLabel='Features'
            referenceLines={[
              {
                value: targetFeaturesPerMonth,
                label: 'Target',
                color: '#4caf50',
                strokeDasharray: '5 5',
              },
            ]}
          />
        </Grid>

        {/* Feature Velocity Indicator */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title='Delivery Velocity'
              subheader='Features + pull requests per month'
              avatar={<Timeline color='primary' />}
            />
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant='h2' color='primary.main'>
                  {currentMonthFeatures}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  This Month
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='body2'>Target:</Typography>
                  <Typography variant='body2' fontWeight='bold'>
                    {targetFeaturesPerMonth}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='body2'>Achievement:</Typography>
                  <Typography
                    variant='body2'
                    fontWeight='bold'
                    color={
                      achievementPercentage >= 100
                        ? 'success.main'
                        : 'warning.main'
                    }
                  >
                    {achievementPercentage.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant='determinate'
                  value={Math.min(achievementPercentage, 100)}
                  color={achievementPercentage >= 100 ? 'success' : 'warning'}
                />

                {/* Additional velocity insights */}
                <Box
                  sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                  >
                    Trend:{' '}
                    {data.kpis?.featuresDelivered?.trend === 'up'
                      ? '↗️ Improving'
                      : data.kpis?.featuresDelivered?.trend === 'down'
                        ? '↘️ Declining'
                        : '➡️ Stable'}
                  </Typography>
                  {data.kpis?.featuresDelivered?.changePercentage && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      display='block'
                    >
                      {Math.abs(
                        data.kpis.featuresDelivered.changePercentage
                      ).toFixed(1)}
                      % vs last month
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
